import { getRefreshTokenWithRedirect, isUnAuthorizedError } from 'util/auth';
import Constants from 'constants/index';
import { createApi } from '@reduxjs/toolkit/query/react';
import { fetchBaseQuery } from '@reduxjs/toolkit/query';

// setup our custom base query fn
const baseQuery = fetchBaseQuery({
  baseUrl: Constants.PATHWAY_API_URL,
  prepareHeaders: (headers, { getState }) => {
    const webToken = JSON.parse(localStorage.getItem('okta-token-storage'))
      ?.accessToken?.accessToken;
    const iOSToken = window.accessToken;
    const reactNativeWebView = window.ReactNativeWebView;
    let androidToken;
    if (
      reactNativeWebView &&
      typeof reactNativeWebView.injectedObjectJson === 'function'
    ) {
      androidToken = JSON.parse(
        reactNativeWebView.injectedObjectJson(),
      ).accessToken;
    }
    const accessToken = webToken || iOSToken || androidToken;
    if (accessToken) {
      headers.set('authorization', `Bearer ${accessToken}`);
    }
    return headers;
  },
  credentials: 'include',
});

// wrap our base query fn with  addtl logic to handle reauth
const baseQueryWithReAuth = async (args, api, extraOptions) => {
  const result = await baseQuery(args, api, extraOptions);
  const { error } = result;
  // if 401 then redirect to login by using the logout() action from marks pr
  // this should include both the token expiration case (3 hr timeout)
  // as well as the "issuer not supported case" - a user wo pway2 okta
  if (isUnAuthorizedError(error)) {
    // debugger; window.location = '/';

    // error.meta is totally legit and allows us not to have to use redux, phew ʕっ•ᴥ•ʔっ (bear hug)
    // https://redux-toolkit.js.org/rtk-query/usage/customizing-queries#adding-meta-information-to-queries
    error.meta = { refreshTokenError: true };
    getRefreshTokenWithRedirect();
    // :) wish we could do the below & handle token refresh right here but dont know endpoints to use
    // try to get a new token
    // const refreshResult = await baseQuery('/refreshToken', api, extraOptions);
    //  if (refreshResult.data) {
    // store the new token
    // api.dispatch(tokenReceived(refreshResult.data));
    // retry the initial query
    // result = await baseQuery(args, api, extraOptions);
    // }
    // else api.dispatch(loggedOut());
  }
  // handle 403 case: where user is logged in but doesnt have permission
  // for example read only perm. and tries a post
  return result;
};

/*
// can also wrap with retry logic when we need to
const baseQueryWithRetry = retry(fetchBaseQuery({ baseUrl: '/' }), {
  maxRetries: 3,
})
*/

// Define a service using a base URL and expected endpoints
export const pathwayApi = createApi({
  reducerPath: 'pathwayApi',
  baseQuery: baseQueryWithReAuth,
  endpoints: builder => ({
    addQuiz: builder.mutation({
      query: body => ({
        url: `${Constants.PATHWAY_API.QUIZ_PATH_NAME}`,
        method: 'POST',
        body,
      }),
    }),
    addTrainingPlan: builder.mutation({
      query: body => ({
        url: `${Constants.PATHWAY_API.V2_PATH_NAME}/${Constants.PATHWAY_API.TRAINING_CHECKLIST_PATH_NAME}`,
        method: 'POST',
        body,
      }),
    }),
    addSearchResultFeedback: builder.mutation({
      query: body => ({
        url: `${Constants.PATHWAY_API.SEARCH_PATH_NAME}/feedback`,
        method: 'POST',
        body,
      }),
    }),
    assignUserToChecklist: builder.mutation({
      query: ({ body, id }) => ({
        url: `${Constants.PATHWAY_API.V2_PATH_NAME}/${Constants.PATHWAY_API.TRAINING_CHECKLIST_PATH_NAME}/${id}/${Constants.PATHWAY_API.ASSIGN_PATH_NAME}`,
        method: 'POST',
        body,
      }),
    }),
    assignUserToComplianceCourse: builder.mutation({
      query: ({ body, id }) => ({
        url: `${Constants.PATHWAY_API.COURSES_PATH_NAME}/${Constants.PATHWAY_API.ASSIGN_PATH_NAME}/${id}`,
        method: 'POST',
        body,
      }),
    }),
    deleteCategory: builder.mutation({
      query: ({ countryCode, id }) => ({
        url: `${Constants.PATHWAY_API.CATEGORIES_PATH_NAME}/${countryCode}/${id}`,
        method: 'DELETE',
      }),
    }),
    deleteTrainingPlan: builder.mutation({
      query: id => ({
        url: `${Constants.PATHWAY_API.V2_PATH_NAME}/${Constants.PATHWAY_API.TRAINING_CHECKLIST_PATH_NAME}/${id}`,
        method: 'DELETE',
      }),
    }),
    deleteQuiz: builder.mutation({
      query: quizId => ({
        url: `${Constants.PATHWAY_API.QUIZ_PATH_NAME}/${quizId}`,
        method: 'DELETE',
      }),
    }),
    duplicateTrainingPlan: builder.mutation({
      query: ({ checklistId, name, ownerId }) => {
        return {
          url: `${Constants.PATHWAY_API.V2_PATH_NAME}/${Constants.PATHWAY_API.TRAINING_CHECKLIST_PATH_NAME}/${checklistId}`,
          method: 'POST',
          body: { ownerId, name },
        };
      },
    }),
    duplicateTemplateTrainingPlan: builder.mutation({
      query: ({ checklistId, name, ownerId }) => {
        return {
          url: `${Constants.PATHWAY_API.V2_PATH_NAME}/${Constants.PATHWAY_API.TRAINING_CHECKLIST_PATH_NAME}/${Constants.PATHWAY_API.TRAINING_TEMPLATE_PATH_NAME}/${checklistId}`,
          method: 'POST',
          body: { ownerId, name },
        };
      },
    }),
    disableCourse: builder.mutation({
      query: ({ operatorId, courseId }) => {
        return {
          url: `${Constants.PATHWAY_API.COURSES_PATH_NAME}/${Constants.PATHWAY_API.SETTINGS_PATH_NAME}/${courseId}/${Constants.PATHWAY_API.DISABLE_PATH_NAME}?${Constants.PATHWAY_API.OPERATOR_ID_PATH_NAME}=${operatorId}`,
          method: 'POST',
        };
      },
    }),
    editCategory: builder.mutation({
      query: body => ({
        url: `${Constants.PATHWAY_API.CATEGORIES_PATH_NAME}`,
        method: 'PUT',
        body,
      }),
    }),
    enableCourse: builder.mutation({
      query: ({ operatorId, courseId }) => {
        return {
          url: `${Constants.PATHWAY_API.COURSES_PATH_NAME}/${Constants.PATHWAY_API.SETTINGS_PATH_NAME}/${courseId}/${Constants.PATHWAY_API.ENABLE_PATH_NAME}?${Constants.PATHWAY_API.OPERATOR_ID_PATH_NAME}=${operatorId}`,
          method: 'POST',
        };
      },
    }),
    getIsCourseEnabled: builder.query({
      query: operatorId =>
        `${Constants.PATHWAY_API.COURSES_PATH_NAME}/${Constants.PATHWAY_API.SETTINGS_PATH_NAME}?operatorId=${operatorId}`,
    }),
    getAssignedChecklists: builder.query({
      query: () =>
        `${Constants.PATHWAY_API.TRAINING_PATH_NAME}/${Constants.PATHWAY_API.TRAINING_CHECKLIST_PATH_NAME}/assigned?version=V2`,
    }),
    getAssignableChecklists: builder.query({
      query: () =>
        `${Constants.PATHWAY_API.V2_PATH_NAME}/${Constants.PATHWAY_API.TRAINING_CHECKLIST_PATH_NAME}/assignable`,
    }),
    getEditableChecklists: builder.query({
      query: () =>
        `${Constants.PATHWAY_API.V2_PATH_NAME}/${Constants.PATHWAY_API.TRAINING_CHECKLIST_PATH_NAME}/editable`,
    }),
    getCourses: builder.query({
      query: ({ type }) =>
        `${Constants.PATHWAY_API.COURSES_PATH_NAME}?type=${type}`,
    }),
    getAssignedCourses: builder.query({
      query: ({ courseId, location }) =>
        `${Constants.PATHWAY_API.COURSES_PATH_NAME}/${Constants.ROUTE_PATH_NAMES.ASSIGNED_PATH_NAME}/${courseId}?location=${location}`,
    }),
    getCourseReport: builder.query({
      query: ({ courseId, location }) =>
        `${Constants.PATHWAY_API.COURSES_PATH_NAME}/${Constants.PATHWAY_API.REPORT_PATH_NAME}/${courseId}?location=${location}`,
    }),
    getAssignedStatus: builder.query({
      query: ({ checklist, location }) => {
        if (location) {
          return `${Constants.PATHWAY_API.V2_PATH_NAME}/${Constants.PATHWAY_API.TRAINING_CHECKLIST_PATH_NAME}/${Constants.ROUTE_PATH_NAMES.ASSIGNED_PATH_NAME}/${checklist}?locationIds=${location}`;
        } else {
          return `${Constants.PATHWAY_API.V2_PATH_NAME}/${Constants.PATHWAY_API.TRAINING_CHECKLIST_PATH_NAME}/${Constants.ROUTE_PATH_NAMES.ASSIGNED_PATH_NAME}/${checklist}`;
        }
      },
    }),
    getCategories: builder.query({
      query: () => Constants.PATHWAY_API.CATEGORIES_PATH_NAME,
    }),
    getCategoryById: builder.query({
      query: id => `${Constants.PATHWAY_API.CATEGORIES_PATH_NAME}/${id}`,
    }),
    getAdminCategoryById: builder.query({
      query: ({ id, countryCode }) =>
        `${Constants.PATHWAY_API.CATEGORIES_PATH_NAME}/${id}?isAdmin=true&country=${countryCode}`,
    }),
    getAdminCategories: builder.query({
      query: ({ countryCode }) =>
        `${Constants.PATHWAY_API.CATEGORIES_PATH_NAME}?isAdmin=true&country=${countryCode}`,
    }),
    editCategories: builder.mutation({
      query: categoriesArray => ({
        url: `${Constants.PATHWAY_API.CATEGORIES_PATH_NAME}`,
        method: 'POST',
        body: categoriesArray,
      }),
    }),
    getAssignedChecklistById: builder.query({
      query: id =>
        `${Constants.PATHWAY_API.V2_PATH_NAME}/${Constants.PATHWAY_API.TRAINING_CHECKLIST_PATH_NAME}/${Constants.ROUTE_PATH_NAMES.ASSIGNED_PATH_NAME}/${id}`,
    }),
    getCompliancePlanUrl: builder.query({
      query: courseId =>
        `${Constants.PATHWAY_API.COURSES_PATH_NAME}/${Constants.PATHWAY_API.SQSSO_PATH_NAME}/${courseId}`,
    }),
    getDocument: builder.query({
      query: id => `${Constants.PATHWAY_API.DOCUMENTS_PATH_NAME}/${id}`,
    }),
    getDocumentCookie: builder.query({
      query: () => `${Constants.PATHWAY_API.DOCUMENTS_PATH_NAME}/cookie`,
    }),
    getSubCategories: builder.query({
      query: id => `${Constants.PATHWAY_API.CATEGORIES_PATH_NAME}/${id}`,
    }),
    getIcons: builder.query({
      query: () =>
        `${Constants.PATHWAY_API.ICONS_PATH_NAME}/${Constants.PATHWAY_API.CATEGORIES_PATH_NAME}`,
    }),
    getSearchResults: builder.query({
      query: ({ query, page, pageSize, language }) =>
        `${Constants.PATHWAY_API.SEARCH_PATH_NAME}/?query=${query}&page=${page}&pageSize=${pageSize}&language=${language}`,
    }),
    getLeadershipTrainings: builder.query({
      query: () => `${Constants.PATHWAY_API.LEADERSHIP_TRAINING_PATH_NAME}`,
    }),
    postLeadershipTrainingLike: builder.mutation({
      query: ({ episodeId }) => ({
        url: `${Constants.PATHWAY_API.LEADERSHIP_TRAINING_PATH_NAME}/${Constants.PATHWAY_API.LIKE_PATH_NAME}/${episodeId}`,
        method: 'POST',
      }),
    }),
    deleteLeadershipTrainingLike: builder.mutation({
      query: ({ episodeId }) => ({
        url: `${Constants.PATHWAY_API.LEADERSHIP_TRAINING_PATH_NAME}/${Constants.PATHWAY_API.LIKE_PATH_NAME}/${episodeId}`,
        method: 'DELETE',
      }),
    }),
    getOperators: builder.query({
      query: () =>
        `${Constants.PATHWAY_API.USERS_PATH_NAME}/${Constants.PATHWAY_API.OPERATORS_PATH_NAME}`,
    }),
    getReportsPlans: builder.query({
      query: ({ userId }) =>
        `${Constants.PATHWAY_API.REPORTS_PATH_NAME}/${Constants.PATHWAY_API.TRAINING_PATH_NAME}/${Constants.PATHWAY_API.REPORTS_TEAM_MEMBER_PATH_NAME}/${userId}`,
    }),
    getReportsTeamMembers: builder.query({
      query: () =>
        `${Constants.PATHWAY_API.REPORTS_PATH_NAME}/${Constants.PATHWAY_API.TRAINING_PATH_NAME}/${Constants.PATHWAY_API.REPORTS_TEAM_MEMBERS_PATH_NAME}`,
    }),
    getQuiz: builder.query({
      query: quizId => `${Constants.PATHWAY_API.QUIZ_PATH_NAME}/${quizId}`,
    }),
    getQuizzes: builder.query({
      query: () => `${Constants.PATHWAY_API.QUIZ_PATH_NAME}`,
    }),
    getTeamMembers: builder.query({
      query: ({ locations }) => {
        if (locations) {
          return `${Constants.PATHWAY_API.USERS_PATH_NAME}/${Constants.PATHWAY_API.LIST_PATH_NAME}?locations=${locations}`;
        } else {
          return `${Constants.PATHWAY_API.USERS_PATH_NAME}/${Constants.PATHWAY_API.LIST_PATH_NAME}`;
        }
      },
    }),
    getTemplates: builder.query({
      query: ({ locations }) =>
        `${Constants.PATHWAY_API.TRAINING_PATH_NAME}/${Constants.PATHWAY_API.TRAINING_CHECKLIST_PATH_NAME}/templates?locations=${locations}`,
    }),
    getTrainingPlan: builder.query({
      query: params => {
        if (params?.id) {
          return `${Constants.PATHWAY_API.V2_PATH_NAME}/${Constants.PATHWAY_API.TRAINING_CHECKLIST_PATH_NAME}/${params.id}`;
        } else if (params?.myPlans) {
          return `${Constants.PATHWAY_API.TRAINING_PATH_NAME}/${Constants.PATHWAY_API.TRAINING_CHECKLIST_PATH_NAME}/${Constants.ROUTE_PATH_NAMES.ASSIGNED_PATH_NAME}?version=V2`;
        } else {
          return `${Constants.PATHWAY_API.V2_PATH_NAME}/${Constants.PATHWAY_API.TRAINING_CHECKLIST_PATH_NAME}/${Constants.ROUTE_PATH_NAMES.ALL_PATH_NAME}`;
        }
      },
      keepUnusedDataFor: 0,
    }),
    resetTrainingPlan: builder.mutation({
      query: ({ checklistId, userIds }) => ({
        url: `${Constants.PATHWAY_API.V2_PATH_NAME}/${Constants.PATHWAY_API.TRAINING_CHECKLIST_PATH_NAME}/${Constants.PATHWAY_API.RESET_PATH_NAME}`,
        method: 'POST',
        body: {
          userIds: userIds,
          checklistId: checklistId,
        },
      }),
    }),
    translateText: builder.mutation({
      query: ({ text, source, target }) => ({
        url: `${Constants.PATHWAY_API.SERVICE_PATH_NAME}/${Constants.PATHWAY_API.TRANSLATE_PATH_NAME}`,
        method: 'POST',
        body: {
          source,
          target,
          text,
        },
      }),
    }),
    getUserPreferences: builder.query({
      query: () =>
        `${Constants.PATHWAY_API.DOCUMENTS_PATH_NAME}/${Constants.PATHWAY_API.USERS_PATH_NAME}/${Constants.PATHWAY_API.PREFERENCES_PATH_NAME}`,
    }),
    unassignUserToChecklist: builder.mutation({
      query: ({ body, id }) => ({
        url: `${Constants.PATHWAY_API.V2_PATH_NAME}/${Constants.PATHWAY_API.TRAINING_CHECKLIST_PATH_NAME}/${id}/${Constants.PATHWAY_API.ASSIGN_PATH_NAME}`,
        method: 'DELETE',
        body,
      }),
    }),
    updateChecklistStatus: builder.mutation({
      query: ({ checklist, status, userIds }) => {
        return {
          url: `${Constants.PATHWAY_API.V2_PATH_NAME}/${Constants.PATHWAY_API.TRAINING_CHECKLIST_PATH_NAME}/${checklist}/${Constants.PATHWAY_API.STATUS_PATH_NAME}`,
          method: 'POST',
          body: {
            status: status,
            ...(userIds && {
              training: {
                userIds: userIds,
              },
            }),
          },
        };
      },
    }),
    updateChecklistStepHeartbeat: builder.mutation({
      query: ({ checklistId, stepId, interval, userIds }) => {
        return {
          url: `${Constants.PATHWAY_API.V2_PATH_NAME}/${Constants.PATHWAY_API.TRAINING_CHECKLIST_PATH_NAME}/${checklistId}/${Constants.PATHWAY_API.STEPS_PATH_NAME}/${stepId}/${Constants.PATHWAY_API.HEARTBEAT_PATH_NAME}`,
          method: 'POST',
          body: {
            interval: interval,
            ...(userIds && {
              training: {
                userIds: userIds,
              },
            }),
          },
        };
      },
    }),
    updateChecklistStepStatus: builder.mutation({
      query: ({ checklistId, stepId, status, userIds }) => {
        return {
          url: `${Constants.PATHWAY_API.V2_PATH_NAME}/${Constants.PATHWAY_API.TRAINING_CHECKLIST_PATH_NAME}/${checklistId}/${Constants.PATHWAY_API.STEPS_PATH_NAME}/${stepId}/${Constants.PATHWAY_API.STATUS_PATH_NAME}`,
          method: 'POST',
          body: {
            status: status,
            ...(userIds && {
              training: {
                userIds: userIds,
              },
            }),
          },
        };
      },
    }),
    updateComplianceHeartbeat: builder.mutation({
      query: ({ courseId, interval, userIds }) => {
        return {
          url: `${Constants.PATHWAY_API.COURSES_PATH_NAME}/${courseId}/${Constants.PATHWAY_API.HEARTBEAT_PATH_NAME}`,
          method: 'POST',
          body: {
            interval: interval,
            ...(userIds && {
              training: {
                userIds: userIds,
              },
            }),
          },
        };
      },
    }),
    updateDueDate: builder.mutation({
      query: ({ body, id }) => ({
        url: `${Constants.PATHWAY_API.V2_PATH_NAME}/${Constants.PATHWAY_API.TRAINING_CHECKLIST_PATH_NAME}/${id}/${Constants.PATHWAY_API.DUE_DATE_PATH_NAME}`,
        method: 'POST',
        body,
      }),
    }),
    updateTrainingPlan: builder.mutation({
      query: body => ({
        url: `${Constants.PATHWAY_API.V2_PATH_NAME}/${Constants.PATHWAY_API.TRAINING_CHECKLIST_PATH_NAME}`,
        method: 'PUT',
        body,
      }),
    }),
    updateQuiz: builder.mutation({
      query: body => ({
        url: `${Constants.PATHWAY_API.QUIZ_PATH_NAME}`,
        method: 'PUT',
        body,
      }),
    }),
    updateTrainingPlanStatus: builder.mutation({
      query: ({ checklistId, status, ...rest }) => ({
        url: `${Constants.PATHWAY_API.V2_PATH_NAME}/${Constants.PATHWAY_API.TRAINING_CHECKLIST_PATH_NAME}/${checklistId}/${status}`,
        method: 'POST',
        body: rest,
      }),
    }),
    updateUserPreferences: builder.mutation({
      query: body => ({
        url: `${Constants.PATHWAY_API.USERS_PATH_NAME}/${Constants.PATHWAY_API.PREFERENCES_PATH_NAME}`,
        method: 'POST',
        body,
      }),
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
//
export const {
  useAddSearchResultFeedbackMutation,
  useGetSearchResultsQuery,
  useAddTrainingPlanMutation,
  useAddQuizMutation,
  useAssignUserToChecklistMutation,
  useAssignUserToComplianceCourseMutation,
  useDeleteCategoryMutation,
  useDeleteTrainingPlanMutation,
  useDeleteQuizMutation,
  useDisableCourseMutation,
  useDuplicateTrainingPlanMutation,
  useDuplicateTemplateTrainingPlanMutation,
  useEditCategoryMutation,
  useEnableCourseMutation,
  useGetAssignableChecklistsQuery,
  useGetAssignedChecklistsQuery,
  useGetAssignedStatusQuery,
  useGetCategoriesQuery,
  useGetCategoryByIdQuery,
  useGetSubCategoriesQuery,
  useGetAdminCategoryByIdQuery,
  useGetAdminCategoriesQuery,
  useEditCategoriesMutation,
  useGetAssignedChecklistByIdQuery,
  useGetCompliancePlanUrlQuery,
  useGetDocumentQuery,
  useGetDocumentCookieQuery,
  useGetIconsQuery,
  useGetEditableChecklistsQuery,
  useGetCoursesQuery,
  useGetAssignedCoursesQuery,
  useGetCourseReportQuery,
  useGetIsCourseEnabledQuery,
  useResetTrainingPlanMutation,
  useGetTrainingPlanQuery,
  useGetTemplatesQuery,
  useTranslateTextMutation,
  useGetLeadershipTrainingsQuery,
  usePostLeadershipTrainingLikeMutation,
  useDeleteLeadershipTrainingLikeMutation,
  useGetOperatorsQuery,
  useGetReportsPlansQuery,
  useGetReportsTeamMembersQuery,
  useGetQuizQuery,
  useGetQuizzesQuery,
  useGetTeamMembersQuery,
  useUnassignUserToChecklistMutation,
  useUpdateChecklistStatusMutation,
  useUpdateChecklistStepStatusMutation,
  useUpdateChecklistStepHeartbeatMutation,
  useUpdateComplianceHeartbeatMutation,
  useUpdateDueDateMutation,
  useUpdateTrainingPlanMutation,
  useUpdateQuizMutation,
  useUpdateTrainingPlanStatusMutation,
  useUpdateUserPreferencesMutation,
} = pathwayApi;
